import { Accordion, Card, Placeholder } from "react-bootstrap";
import "./CostProjectionSection.scss";
import { useState } from "react";
import { CostProjectionCard } from "./CostProjectionCard";
import { CostProjectionDetailsModal } from "./CostProjectionDetailsModal";
import { BillingInfoModal } from "./BillingInfoModal";
import { ServerRequestsMoneyDto } from "./shared/api/axios-client";
import { Instant } from "@js-joda/core";
import { Calculator, ChevronDown, ChevronUp, QuestionCircle } from "react-bootstrap-icons";

export type ParkerCostDto = {
  subscriptionParkerId: number;
  parkerName: string;
  startDate: string | null;
  basePrice: number;
  adjustment: number;
  finalPrice: number;
  wasProrated: boolean;
};

export type CostProjectionDto = {
  total: ServerRequestsMoneyDto;
  basePrice: number;
  hasPreviousPeriodActivity: boolean;
  periodStartDate: string;

  groups: {
    groupId: number;
    groupName: string;
    parkerCount: number;
    subtotal: number;

    parkers: {
      name: string;
      startDate: Instant | null;
    }[];

    parkerCosts: ParkerCostDto[];

    appliedTiers: any;

    lineItems: {
      description: string;
      quantity?: number;
      unitPrice?: number;
      total: number;
    }[];
  }[];
};

type Props = {
  current: CostProjectionDto | null;
  next: CostProjectionDto | null;
  isAccountsReceivable: boolean;
  onLoad: () => Promise<void>;
  loading: boolean;
  /** When provided, period end is before this date → card shows "Not yet active". */
  subscriptionStartDate?: string | null;
};

export function CostProjectionSection({
  current,
  next,
  isAccountsReceivable,
  onLoad,
  loading,
  subscriptionStartDate,
}: Props) {
  const [showDetails, setShowDetails] = useState<"current" | "next" | null>(
    null,
  );
  const [lastSelected, setLastSelected] = useState<"current" | "next">("current");
  const [showBillingInfo, setShowBillingInfo] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const openDetails = (variant: "current" | "next") => {
    setLastSelected(variant);
    setShowDetails(variant);
  };

  // Use lastSelected to prevent flicker during close animation
  const activeVariant = showDetails ?? lastSelected;

  const isDataLoaded = current !== null && next !== null;

  const handleAccordionSelect = (eventKey: string | string[] | null | undefined) => {
    if (eventKey === "0") {
      // User wants to expand - expand immediately and trigger load
      setIsExpanded(true);
      if (!isDataLoaded && !loading) {
        onLoad();
      }
    } else {
      // User wants to collapse
      setIsExpanded(false);
    }
  };

  return (
    <>
      <Accordion 
        className="mb-1 cost-projection-accordion" 
        activeKey={isExpanded ? "0" : undefined}
        onSelect={handleAccordionSelect}
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex align-items-center gap-2 w-100 me-3">
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <Calculator size={16} className="text-muted" />
              <span>{isExpanded ? "Hide" : "Show"} Cost Projections</span>
            </div>
          </Accordion.Header>

          <Accordion.Body>
            <div 
              role="button"
              tabIndex={0}
              className="text-muted d-flex align-items-center gap-1 mb-3"
              style={{ cursor: "pointer", width: "fit-content" }}
              onClick={() => setShowBillingInfo(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowBillingInfo(true);
                }
              }}
              title="How billing works"
            >
              <QuestionCircle />
              <span className="small">How billing works</span>
            </div>

            {loading ? (
              <div 
                className="d-grid gap-3" 
                style={{ gridTemplateColumns: "1fr 1fr" }}
              >
                <LoadingCard />
                <LoadingCard />
              </div>
            ) : isDataLoaded ? (
              <div 
                className="d-grid gap-3" 
                style={{ gridTemplateColumns: "1fr 1fr" }}
              >
                <CostProjectionCard
                  title="Current Period"
                  total={current.total.amount!}
                  variant="current"
                  onDetails={() => openDetails("current")}
                  periodStartDate={current.periodStartDate}
                  subscriptionStartDate={subscriptionStartDate ?? undefined}
                />

                <CostProjectionCard
                  title="Next Period"
                  total={next.total.amount!}
                  variant="next"
                  onDetails={() => openDetails("next")}
                  periodStartDate={next.periodStartDate}
                  subscriptionStartDate={subscriptionStartDate ?? undefined}
                />
              </div>
            ) : null}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {isDataLoaded && (
        <CostProjectionDetailsModal
          show={showDetails !== null}
          onHide={() => setShowDetails(null)}
          title={
            activeVariant === "current"
              ? "Current Period Breakdown"
              : "Next Period Breakdown"
          }
          data={activeVariant === "current" ? current : next}
        />
      )}

      <BillingInfoModal
        show={showBillingInfo}
        onHide={() => setShowBillingInfo(false)}
        isAccountsReceivable={isAccountsReceivable}
      />
    </>
  );
}

function LoadingCard() {
  return (
    <Card className="h-100">
      <Card.Body className="d-flex flex-column gap-2">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={6} size="sm" />
        </Placeholder>

        <Placeholder as="div" animation="glow">
          <Placeholder xs={8} size="sm" />
        </Placeholder>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <Placeholder as="div" animation="glow" style={{ width: "40%" }}>
            <Placeholder xs={12} size="sm" />
          </Placeholder>
          <Placeholder as="div" animation="glow" style={{ width: "30%" }}>
            <Placeholder xs={12} size="lg" />
          </Placeholder>
        </div>

        <div className="flex-grow-1" />

        <Placeholder.Button variant="outline-secondary" xs={12} size="sm" />
      </Card.Body>
    </Card>
  );
}
